/**
 * ProtectedRoute Component
 *
 * Protects routes that require authentication and/or specific permissions.
 * Redirects to login page if user is not authenticated.
 * Redirects to unauthorized page if user lacks required permissions.
 *
 * @param {React.ReactNode} children - The component to render if authorized
 * @param {string} requiredModule - Required permission module (e.g., 'role', 'user')
 * @param {string} requiredAction - Required permission action (e.g., 'viewRole', 'viewUser')
 */

import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import authService from '../../services/authService';
import { hasPermission } from '../../utils/permissions';
import { selectPermissions, selectLoginLoading } from '../../pages/Login/slice';

function ProtectedRoute({ children, requiredModule, requiredAction }) {
  const { t } = useTranslation();
  const isAuthenticated = authService.isAuthenticated();
  // Subscribe to permissions from Redux state to trigger re-render when permissions are loaded
  const reduxPermissions = useSelector(selectPermissions);
  const isLoadingPermissions = useSelector(selectLoginLoading);

  if (!isAuthenticated) {
    // User is not authenticated, redirect to login
    return <Navigate to="/login" replace />;
  }

  // If permissions are required but not loaded yet, show loading spinner
  // This prevents "Access Denied" flash while permissions are being fetched
  if (requiredModule && requiredAction && !reduxPermissions) {
    // Show loading if actively loading OR if permissions haven't been loaded yet
    if (isLoadingPermissions) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            p: 3,
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {t('protectedRoute.loading')}
          </Typography>
        </Box>
      );
    }

    // If not loading and no permissions, it means permissions failed to load or user has no permissions at all
    // In this case, show Access Denied
  }

  // If permission is required, check if user has it (only when permissions are loaded)
  if (requiredModule && requiredAction) {
    const hasRequiredPermission = hasPermission(requiredModule, requiredAction);

    if (!hasRequiredPermission) {
      // User lacks required permission, show Access Denied page
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
            p: 3,
          }}
        >
          <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {t('protectedRoute.accessDenied')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('protectedRoute.noPermission')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.history.back()}
            id="goBack"
            name="goBack"
            data-testid="access-denied-button-goBack"
          >
            {t('protectedRoute.goBack')}
          </Button>
        </Box>
      );
    }
  }

  // User is authenticated and has required permissions (if any), render the protected content
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredModule: PropTypes.string,
  requiredAction: PropTypes.string,
};

export default ProtectedRoute;
